/**
 * C Side Cart — behavior for sections/c-side-cart.liquid (sl-section-c-side-cart-v1).
 *
 * The drawer never prices anything. Every change goes to the Cart AJAX API with
 * `sections`, Shopify answers with the re-rendered section in the same response,
 * and this file swaps the [data-sc-region] parts of it into the page. The
 * express checkout buttons sit outside those regions, so they are rendered once
 * and never have to be re-initialised.
 *
 * Public API, also used by assets/product-form.js and assets/c-bundle-builder.js:
 *   cart.open(trigger) / cart.close()
 *   cart.refresh({ open })             re-fetch the section, optionally open
 *   cart.addItems(items, { trigger })  POST /cart/add.js, render and open
 *   cart.getSectionsToRender()         product-form.js contract
 *   cart.renderContents(response)      product-form.js contract
 *   cart.setActiveElement(element)     product-form.js contract
 *
 * Document events: listens for `c-side-cart:open` and `c-side-cart:refresh`
 * (detail.open), dispatches `c-side-cart:updated` after every render.
 *
 * Gift reward: after every render, syncGift() runs the instruction the section
 * put on the body region (data-gift-sync): add the gift line, or update lines
 * and the _c_gift cart attribute. Liquid decides; this file only sends it.
 */
(() => {
  if (customElements.get('c-side-cart')) return;

  const BUBBLE_SECTION = 'cart-icon-bubble';
  const LINE_DEBOUNCE = 350;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  const parseJSON = (value, fallback) => {
    try {
      return value ? JSON.parse(value) : fallback;
    } catch (error) {
      return fallback;
    }
  };

  const sameCode = (a, b) => String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

  class CSideCart extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.panel = this.querySelector('[data-sc-panel]');
      this.isOpen = false;
      this.queue = Promise.resolve();
      this.busyCount = 0;
      this.lineTimers = new Map();
      this.lostLine = null;
      this.giftSignature = null;
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.addEventListener('click', (event) => this.onClick(event), { signal });
      this.addEventListener('change', (event) => this.onChange(event), { signal });
      this.addEventListener('submit', (event) => this.onSubmit(event), { signal });
      document.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      document.addEventListener('focusin', (event) => this.onFocusIn(event), { signal });
      document.addEventListener('click', (event) => this.onDocumentClick(event), { signal });
      if (this.dataset.interceptForms === 'true') {
        document.addEventListener('submit', (event) => this.onDocumentSubmit(event), { signal });
      }
      document.addEventListener('c-side-cart:open', () => this.open(), { signal });
      document.addEventListener('c-side-cart:refresh', (event) => this.refresh({ open: Boolean(event.detail?.open) }), { signal });
      // Coming back from checkout through the back button restores a stale page.
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.refresh();
      }, { signal });

      if (window.Shopify?.designMode) this.bindThemeEditor(signal);
      // The cart may already qualify (or stop qualifying) when the page loads.
      this.syncGift();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.lineTimers.forEach((timer) => clearTimeout(timer));
      if (this.isOpen) document.body.classList.remove('overflow-hidden-drawer');
    }

    /* ------------------------------------------------------------ Theme editor */
    bindThemeEditor(signal) {
      const isMine = (event) => event.detail?.sectionId === this.sectionId;
      document.addEventListener('shopify:section:select', (event) => isMine(event) && this.open(), { signal });
      document.addEventListener('shopify:section:deselect', (event) => isMine(event) && this.close(), { signal });
      document.addEventListener('shopify:block:select', (event) => isMine(event) && this.open(), { signal });
      // Changing a setting re-renders the section, which replaces this element.
      if (window.cSideCartEditorOpen) this.open();
    }

    /* --------------------------------------------------------------- Open/close */
    open(trigger) {
      if (trigger instanceof HTMLElement) {
        this.activeElement = trigger;
      } else if (!this.isOpen && !this.activeElement && document.activeElement instanceof HTMLElement) {
        this.activeElement = document.activeElement;
      }
      if (this.isOpen) return;

      this.isOpen = true;
      this.classList.add('is-open');
      document.body.classList.add('overflow-hidden-drawer');
      if (window.Shopify?.designMode) window.cSideCartEditorOpen = true;

      // Focus the dialog itself: screen readers announce its label and the next
      // Tab lands on the close button, without a focus ring on open.
      requestAnimationFrame(() => this.panel?.focus({ preventScroll: true }));
      document.dispatchEvent(new CustomEvent('c-side-cart:opened'));
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden-drawer');
      if (window.Shopify?.designMode) window.cSideCartEditorOpen = false;

      const previous = this.activeElement;
      this.activeElement = null;
      if (previous && document.contains(previous)) previous.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('c-side-cart:closed'));
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    /* ------------------------------------------------------------------ Events */
    onDocumentClick(event) {
      const trigger = event.target.closest('#cart-icon-bubble, [data-c-side-cart-open]');
      if (!trigger || event.defaultPrevented) return;
      event.preventDefault();
      this.open(trigger);
      // The page render can be stale (another tab, an app, the back button).
      this.refresh();
    }

    onDocumentSubmit(event) {
      const form = event.target;
      if (event.defaultPrevented || !(form instanceof HTMLFormElement)) return;
      if (!form.matches('form[action*="/cart/add"]') || this.contains(form)) return;
      // Forms that send the shopper somewhere on purpose keep their behavior.
      if (form.querySelector('[name="return_to"]')) return;

      event.preventDefault();
      const formData = new FormData(form);
      const trigger = event.submitter || form.querySelector('[type="submit"]');
      trigger?.setAttribute('aria-disabled', 'true');
      this.addFormData(formData, trigger)
        .catch((error) => {
          if (error.data) {
            this.open(trigger);
            this.showError(error.message);
          } else {
            form.submit();
          }
        })
        .finally(() => trigger?.removeAttribute('aria-disabled'));
    }

    onClick(event) {
      const target = event.target.closest('[data-sc-close], [data-sc-qty], [data-sc-remove], [data-sc-upsell-add], [data-sc-code-remove], [data-sc-undo], [data-sc-gift-add]');
      if (!target || !this.contains(target)) return;

      if (target.hasAttribute('data-sc-close')) {
        event.preventDefault();
        this.close();
        return;
      }

      const line = target.closest('[data-sc-line]');
      if (target.hasAttribute('data-sc-qty') && line) {
        this.stepLine(line, Number(target.dataset.scQty));
      } else if (target.hasAttribute('data-sc-remove') && line) {
        this.updateLine(line, 0);
      } else if (target.hasAttribute('data-sc-upsell-add')) {
        this.addUpsell(target);
      } else if (target.hasAttribute('data-sc-code-remove')) {
        this.removeCode(target.dataset.scCodeRemove);
      } else if (target.hasAttribute('data-sc-undo')) {
        this.undo();
      } else if (target.hasAttribute('data-sc-gift-add')) {
        this.restoreGift();
      }
    }

    onChange(event) {
      const input = event.target.closest('[data-sc-qty-input]');
      if (!input) return;
      const line = input.closest('[data-sc-line]');
      const quantity = Math.max(0, parseInt(input.value, 10) || 0);
      input.value = quantity;
      this.scheduleLine(line, quantity);
    }

    onSubmit(event) {
      const form = event.target.closest('[data-sc-code-form]');
      if (!form) return;
      event.preventDefault();
      const input = form.querySelector('[data-sc-code-input]');
      const code = input?.value.trim();
      if (!code) {
        input?.focus();
        return;
      }
      this.applyCode(code);
    }

    onKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      // Shift+Tab on the first control wraps to the last one. Forward Tab is
      // left alone so it can reach the wallet buttons (they live in shadow
      // roots this list cannot see); onFocusIn catches it when it leaves.
      if (event.key !== 'Tab' || !event.shiftKey) return;
      const focusables = this.focusables();
      if (focusables.length && document.activeElement === focusables[0]) {
        event.preventDefault();
        focusables[focusables.length - 1].focus();
      }
    }

    onFocusIn(event) {
      if (!this.isOpen || this.contains(event.target)) return;
      (this.focusables()[0] || this.panel)?.focus({ preventScroll: true });
    }

    focusables() {
      return [...this.panel.querySelectorAll(FOCUSABLE)].filter((element) => element.getClientRects().length > 0);
    }

    /* ------------------------------------------------------------ Line updates */
    stepLine(line, delta) {
      const input = line.querySelector('[data-sc-qty-input]');
      if (!input) return;
      const current = parseInt(input.value, 10) || 0;
      const next = Math.max(0, current + delta);
      if (next === current) return;
      input.value = next;
      this.scheduleLine(line, next);
    }

    // Quick +/+/+ taps become one request with the final quantity.
    scheduleLine(line, quantity) {
      const key = line.dataset.key;
      clearTimeout(this.lineTimers.get(key));
      this.lineTimers.set(key, setTimeout(() => {
        this.lineTimers.delete(key);
        this.updateLine(line, quantity);
      }, LINE_DEBOUNCE));
    }

    updateLine(element, quantity) {
      const key = element.dataset.key;
      clearTimeout(this.lineTimers.get(key));
      this.lineTimers.delete(key);
      // Another update may have re-rendered the lines since the tap.
      const line = this.findLine(key) || element;

      const before = Number(line.dataset.quantity) || 0;
      if (quantity === before) return Promise.resolve();

      const removed = {
        id: Number(line.dataset.variantId),
        quantity: before - quantity,
        properties: parseJSON(line.dataset.properties, {}),
        sellingPlan: line.dataset.sellingPlan,
      };
      // "pending" means the cart met the rule even if Shopify has not applied the
      // discount yet, so dropping below the rule still deserves the notice.
      const wasQualified = ['unlocked', 'pending'].includes(this.milestone());
      // Removing the gift is remembered on the cart so it is not added back.
      const declinesGift = line.dataset.gift === 'true' && quantity === 0;
      this.giftSignature = null;
      line.classList.add('is-loading');
      this.setBusy(true);

      return this.enqueue(async () => {
        try {
          const sections = { sections: this.sectionIds(), sections_url: window.location.pathname };
          const cart = declinesGift
            ? await this.post(this.url('cartUpdateUrl'), { updates: { [key]: 0 }, attributes: { _c_gift: 'declined' }, ...sections })
            : await this.post(this.url('cartChangeUrl'), { id: key, quantity, ...sections });
          const lost = removed.quantity > 0 && wasQualified && cart.item_count > 0;
          this.lostLine = null;
          this.render(cart.sections);
          if (lost && this.milestone() === 'locked' && this.dataset.lostNotice === 'true') {
            this.lostLine = removed;
            this.syncNotice();
          }
          this.publish(cart);
          this.announce();
        } catch (error) {
          await this.refresh().catch(() => {});
          this.lineError(key, error.message);
        } finally {
          this.findLine(key)?.classList.remove('is-loading');
          this.setBusy(false);
        }
      });
    }

    undo() {
      const line = this.lostLine;
      if (!line) return Promise.resolve();
      const item = { id: line.id, quantity: line.quantity };
      if (line.properties && Object.keys(line.properties).length) item.properties = line.properties;
      if (line.sellingPlan) item.selling_plan = Number(line.sellingPlan);

      this.setBusy(true);
      return this.enqueue(async () => {
        try {
          const response = await this.post(this.url('cartAddUrl'), {
            items: [item],
            sections: this.sectionIds(),
            sections_url: window.location.pathname,
          });
          this.lostLine = null;
          this.render(response.sections);
          this.publish(response);
        } catch (error) {
          this.showError(error.message);
        } finally {
          this.setBusy(false);
        }
      });
    }

    /* -------------------------------------------------------------------- Adds */
    addItems(items, { trigger } = {}) {
      this.giftSignature = null;
      this.setBusy(true);
      return this.enqueue(async () => {
        try {
          const response = await this.post(this.url('cartAddUrl'), {
            items,
            sections: this.sectionIds(),
            sections_url: window.location.pathname,
          });
          this.afterAdd(response, trigger);
          return response;
        } finally {
          this.setBusy(false);
        }
      });
    }

    addFormData(formData, trigger) {
      formData.append('sections', this.sectionIds().join(','));
      formData.append('sections_url', window.location.pathname);
      this.giftSignature = null;
      this.setBusy(true);
      return this.enqueue(async () => {
        try {
          const response = await this.request(this.url('cartAddUrl'), {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
          });
          this.afterAdd(response, trigger);
          return response;
        } finally {
          this.setBusy(false);
        }
      });
    }

    addUpsell(button) {
      if (button.disabled) return;
      button.disabled = true;
      this.addItems([{ id: Number(button.dataset.variantId), quantity: 1 }])
        .catch((error) => {
          this.showError(error.message);
          button.disabled = false;
        });
    }

    afterAdd(response, trigger) {
      // aematic items_added, same call product-form.js makes (snippets/aematic-events.liquid).
      if (typeof window.aematicTrackAdd === 'function') window.aematicTrackAdd(response);
      this.lostLine = null;
      if (response.sections) this.render(response.sections);
      else this.refresh();
      this.publish(response);
      if (this.dataset.openOnAdd !== 'false') this.open(trigger);
    }

    /* --------------------------------------------------- product-form.js contract */
    getSectionsToRender() {
      return this.sectionIds().map((id) => ({ id, section: id, selector: '.shopify-section' }));
    }

    renderContents(response) {
      this.lostLine = null;
      if (response?.sections) this.render(response.sections);
      else this.refresh();
      if (this.dataset.openOnAdd !== 'false') this.open(this.activeElement);
    }

    /* -------------------------------------------------------------------- Gift */
    // The signature stops a loop when Shopify answers the same instruction the
    // same way (sold out, rejected add): it is retried only after a shopper change.
    syncGift() {
      const raw = this.querySelector('[data-sc-region="body"]')?.dataset.giftSync;
      if (!raw) {
        this.giftSignature = null;
        return;
      }
      if (raw === this.giftSignature) return;
      this.giftSignature = raw;
      const sync = parseJSON(raw, null);
      if (!sync) return;

      this.setBusy(true);
      this.enqueue(async () => {
        try {
          const sections = { sections: this.sectionIds(), sections_url: window.location.pathname };
          const response = sync.add
            ? await this.post(this.url('cartAddUrl'), { items: [sync.add], ...sections })
            : await this.post(this.url('cartUpdateUrl'), { updates: sync.updates || {}, attributes: sync.attributes, ...sections });
          this.render(response.sections);
          this.publish(response);
        } catch (error) {
          // The cart stays as it is; the shopper can still check out.
        } finally {
          this.setBusy(false);
        }
      });
    }

    // "Add my free gift" after removing it: clearing the attribute is enough,
    // the next render asks syncGift() to add the line.
    restoreGift() {
      this.giftSignature = null;
      this.setBusy(true);
      return this.enqueue(async () => {
        try {
          const cart = await this.post(this.url('cartUpdateUrl'), {
            attributes: { _c_gift: '' },
            sections: this.sectionIds(),
            sections_url: window.location.pathname,
          });
          this.render(cart.sections);
          this.publish(cart);
        } catch (error) {
          this.showError(error.message);
        } finally {
          this.setBusy(false);
        }
      });
    }

    /* ---------------------------------------------------------- Discount codes */
    applyCode(code) {
      this.setBusy(true);
      this.codeError('');
      return this.enqueue(async () => {
        try {
          // Read every code on the cart, including ones Shopify is not applying
          // yet, so adding a new code never drops one the shopper already had.
          const current = await this.getCart();
          const existing = (current.discount_codes || []).map((entry) => entry.code);
          const codes = existing.some((entry) => sameCode(entry, code)) ? existing : [...existing, code];
          const cart = await this.post(this.url('cartUpdateUrl'), {
            discount: codes.join(','),
            sections: this.sectionIds(),
            sections_url: window.location.pathname,
          });
          const entry = (cart.discount_codes || []).find((item) => sameCode(item.code, code));
          if (entry?.applicable) {
            this.render(cart.sections);
            this.publish(cart);
            this.announce();
            return;
          }

          // Not applied (invalid, expired or not combinable): put the cart back.
          const restored = await this.post(this.url('cartUpdateUrl'), {
            discount: existing.join(','),
            sections: this.sectionIds(),
            sections_url: window.location.pathname,
          });
          this.render(restored.sections);
          this.publish(restored);
          this.codeError(this.dataset.codeError, code);
        } catch (error) {
          this.codeError(this.dataset.codeError, code);
        } finally {
          this.setBusy(false);
        }
      });
    }

    removeCode(code) {
      this.setBusy(true);
      return this.enqueue(async () => {
        try {
          const current = await this.getCart();
          const codes = (current.discount_codes || []).map((entry) => entry.code).filter((entry) => !sameCode(entry, code));
          const cart = await this.post(this.url('cartUpdateUrl'), {
            discount: codes.join(','),
            sections: this.sectionIds(),
            sections_url: window.location.pathname,
          });
          this.render(cart.sections);
          this.publish(cart);
          this.announce();
        } catch (error) {
          this.showError(error.message);
        } finally {
          this.setBusy(false);
        }
      });
    }

    codeError(message, code) {
      const error = this.querySelector('[data-sc-code-error]');
      if (!error) return;
      error.textContent = message || '';
      error.hidden = !message;
      if (message && code) {
        const input = this.querySelector('[data-sc-code-input]');
        if (input) input.value = code;
      }
    }

    /* --------------------------------------------------------------- Rendering */
    refresh({ open = false } = {}) {
      return this.enqueue(async () => {
        const root = this.dataset.rootUrl || '/';
        const separator = root.endsWith('/') ? '' : '/';
        const sections = await this.request(`${root}${separator}?sections=${this.sectionIds().join(',')}`, {
          headers: { Accept: 'application/json' },
        });
        this.render(sections);
        if (open) this.open();
        return sections;
      });
    }

    render(sections) {
      if (!sections) return;
      const html = sections[this.sectionId];
      if (html) this.swap(html);

      const bubbleHtml = sections[BUBBLE_SECTION];
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubbleHtml && bubble) {
        const source = new DOMParser().parseFromString(bubbleHtml, 'text/html').querySelector('.shopify-section');
        if (source) bubble.innerHTML = source.innerHTML;
      }

      document.dispatchEvent(new CustomEvent('c-side-cart:updated', { detail: { sections } }));
    }

    swap(html) {
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('c-side-cart');
      if (!next) return;

      const active = document.activeElement;
      const focusKey = this.contains(active) ? active.closest('[data-sc-focus]')?.dataset.scFocus : null;
      const openBundles = [...this.querySelectorAll('[data-sc-components][open]')]
        .map((details) => details.closest('[data-sc-line]')?.dataset.key)
        .filter(Boolean);
      const previousFill = this.querySelector('[data-sc-fill]')?.style.getPropertyValue('--sc-fill');

      next.querySelectorAll('[data-sc-region]').forEach((fresh) => {
        const current = this.querySelector(`[data-sc-region="${fresh.dataset.scRegion}"]`);
        if (current) current.replaceWith(document.adoptNode(fresh));
      });

      const footer = this.querySelector('[data-sc-footer]');
      const nextFooter = next.querySelector('[data-sc-footer]');
      if (footer && nextFooter) footer.hidden = nextFooter.hidden;

      openBundles.forEach((key) => {
        const details = this.findLine(key)?.querySelector('[data-sc-components]');
        if (details) details.open = true;
      });
      this.animateFill(previousFill);
      this.syncNotice();
      this.hideError();
      this.syncGift();

      if (focusKey && this.isOpen) {
        const target = this.querySelector(`[data-sc-focus="${CSS.escape(focusKey)}"]`);
        (target || this.querySelector('[data-sc-focus="close"]') || this.panel)?.focus({ preventScroll: true });
      }
    }

    // Start the new bar at the old width so the change animates.
    animateFill(previous) {
      const fill = this.querySelector('[data-sc-fill]');
      if (!fill || !previous) return;
      const target = fill.style.getPropertyValue('--sc-fill');
      if (!target || target === previous) return;
      fill.style.setProperty('--sc-fill', previous);
      fill.getBoundingClientRect();
      requestAnimationFrame(() => fill.style.setProperty('--sc-fill', target));
    }

    syncNotice() {
      const notice = this.querySelector('[data-sc-notice]');
      if (notice) notice.hidden = !this.lostLine;
    }

    /* ----------------------------------------------------------------- Helpers */
    milestone() {
      return this.querySelector('[data-sc-region="body"]')?.dataset.milestone;
    }

    sectionIds() {
      return [this.sectionId, BUBBLE_SECTION];
    }

    url(name) {
      return `${this.dataset[name]}.js`;
    }

    findLine(key) {
      return this.querySelector(`[data-sc-line][data-key="${CSS.escape(key)}"]`);
    }

    getCart() {
      return this.request(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } });
    }

    post(url, body) {
      return this.request(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
    }

    async request(url, options) {
      const response = await fetch(url, options);
      const data = await response.json().catch(() => ({}));
      // Cart errors come back as { status, message, description } (422 etc.).
      if (!response.ok || (data && typeof data.status === 'number')) {
        const error = new Error(data.description || data.message || this.dataset.errorText);
        error.data = data;
        throw error;
      }
      return data;
    }

    // Cart requests run one at a time so responses never land out of order.
    enqueue(task) {
      const run = this.queue.then(task, task);
      this.queue = run.catch(() => {});
      return run;
    }

    setBusy(busy) {
      this.busyCount = Math.max(0, this.busyCount + (busy ? 1 : -1));
      const isBusy = this.busyCount > 0;
      this.classList.toggle('is-busy', isBusy);
      this.panel?.setAttribute('aria-busy', String(isBusy));
    }

    publish(cart) {
      // Theme pub/sub (assets/pubsub.js): keeps the aematic cart cache and any
      // other cart-update subscriber in sync.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'c-side-cart', cartData: cart });
      }
    }

    // The text ("New subtotal: $X") is rendered by Liquid on the body region.
    announce() {
      const status = this.querySelector('[data-sc-status]');
      const message = this.querySelector('[data-sc-region="body"]')?.dataset.announce;
      if (!status || !message) return;
      status.textContent = '';
      requestAnimationFrame(() => {
        status.textContent = message;
      });
    }

    lineError(key, message) {
      const error = this.findLine(key)?.querySelector('[data-sc-line-error]');
      if (error) {
        error.textContent = message;
        error.hidden = false;
      } else {
        this.showError(message);
      }
    }

    showError(message) {
      const error = this.querySelector('[data-sc-error]');
      if (!error) return;
      error.textContent = message || this.dataset.errorText;
      error.hidden = false;
    }

    hideError() {
      const error = this.querySelector('[data-sc-error]');
      if (error) error.hidden = true;
    }
  }

  customElements.define('c-side-cart', CSideCart);
})();
